import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AddressAutocompleteInput.css';

interface AzureMapsSuggestion {
  address: {
    streetNumber?: string;
    streetName?: string;
    municipality?: string;
    countrySubdivision?: string;
    postalCode?: string;
    freeformAddress: string;
  };
}

export interface AddressParts {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressAutocompleteInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (parts: AddressParts) => void;
}

const MAPS_KEY = process.env.REACT_APP_AZURE_MAPS_KEY;

export default function AddressAutocompleteInput({
  id,
  value,
  onChange,
  onAddressSelect,
}: AddressAutocompleteInputProps) {
  const [suggestions, setSuggestions] = useState<AzureMapsSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!MAPS_KEY || query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      const url =
        `https://atlas.microsoft.com/search/address/json` +
        `?api-version=1.0` +
        `&subscription-key=${MAPS_KEY}` +
        `&query=${encodeURIComponent(query)}` +
        `&countrySet=US` +
        `&limit=5` +
        `&typeahead=true`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const results: AzureMapsSuggestion[] = data.results ?? [];
      setSuggestions(results);
      setOpen(results.length > 0);
      setActiveIndex(-1);
    } catch {
      // silently fail — user can still type manually
    }
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  }

  function handleSelect(s: AzureMapsSuggestion) {
    const a = s.address;
    const streetParts = [a.streetNumber, a.streetName].filter(Boolean);
    const street = streetParts.length > 0
      ? streetParts.join(' ')
      : a.freeformAddress.split(',')[0].trim();
    onAddressSelect({
      street,
      city: a.municipality ?? '',
      state: a.countrySubdivision ?? '',
      zip: a.postalCode ?? '',
    });
    setOpen(false);
    setSuggestions([]);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // No API key configured — render plain input so the rest of the form still works
  if (!MAPS_KEY) {
    return (
      <input
        id={id}
        className="form-input"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        maxLength={300}
        autoComplete="street-address"
      />
    );
  }

  return (
    <div className="address-autocomplete" ref={containerRef}>
      <input
        id={id}
        className="form-input"
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        maxLength={300}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${id}-listbox`}
        aria-activedescendant={activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined}
      />
      {open && (
        <ul
          id={`${id}-listbox`}
          className="address-autocomplete-dropdown"
          role="listbox"
          aria-label="Address suggestions"
        >
          {suggestions.map((s, i) => (
            <li
              key={i}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-autocomplete-option${i === activeIndex ? ' active' : ''}`}
              onMouseDown={() => handleSelect(s)}
            >
              {s.address.freeformAddress}
            </li>
          ))}
          <li className="address-autocomplete-attribution" aria-hidden="true">
            Powered by Azure Maps
          </li>
        </ul>
      )}
    </div>
  );
}
